import type { DisplayLabel, LocalizedString } from "@/types";

/**
 * ``projects.name`` is a TEXT column that may hold either a plain
 * string or a JSON-encoded ``LocalizedString`` (``{"en":…,"es":…}``).
 * The read path already resolves both shapes (``resolveLocalized``),
 * but the Settings form used to bind the RAW column value — showing
 * the admin literal JSON (bug spotted verifying PR-0 on staging).
 *
 * These helpers give the form a proper ``DisplayLabel`` round-trip:
 * parse on load, edit via ``BilingualInput``, serialize on save.
 */
export function parseProjectName(
  raw: string | null | undefined,
): DisplayLabel {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (trimmed.startsWith("{")) {
    // Not every "{…" string is our LocalizedString JSON — a plain name
    // could theoretically start with a brace. A failed parse simply
    // means "treat it as the plain string it is" (format detection,
    // not error suppression).
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        ("en" in parsed || "es" in parsed)
      ) {
        return parsed as LocalizedString;
      }
    } catch {
      /* plain string that happens to start with "{" */
    }
  }
  return raw;
}

export function serializeProjectName(value: DisplayLabel): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  return JSON.stringify(value);
}

export function hasProjectName(value: DisplayLabel): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return Boolean(value.en?.trim() || value.es?.trim());
}
